import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, adminAuth } from "@/lib/api";
import { formatApiError } from "@/lib/formatError";
import { TopNav, Footer } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Loader2, UserPlus, RefreshCw, Power, PowerOff, Copy, AlertTriangle, Sparkles, Clock } from "lucide-react";
import { toast } from "sonner";

const Badge = ({ tone, children }) => {
  const map = {
    active: "bg-emerald-50 text-emerald-700 border-emerald-200",
    pending: "bg-amber-50 text-amber-800 border-amber-200",
    accepted: "bg-emerald-50 text-emerald-700 border-emerald-200",
    inactive: "bg-slate-100 text-slate-500 border-slate-200",
    suspended: "bg-red-50 text-red-700 border-red-200",
    practitioner_lite: "bg-purple-50 text-purple-900 border-purple-100",
    practitioner_pro: "bg-purple-900 text-white border-purple-900",
  };
  return <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${map[tone] || "bg-slate-100 text-slate-500 border-slate-200"}`}>{children}</span>;
};

export default function AdminPractitioners() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", clinic_name: "", role: "practitioner_lite", phone: "", notes: "" });
  const [inviting, setInviting] = useState(false);
  const [senderStatus, setSenderStatus] = useState(null);

  useEffect(() => {
    if (!adminAuth.getToken()) { navigate("/admin/login"); return; }
    if (!adminAuth.isSuperAdmin()) { navigate("/admin"); return; }
    load();
    api.get("/admin/email-sender-status").then(({ data }) => setSenderStatus(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/practitioners");
      setRows(data);
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        adminAuth.clear(); navigate("/admin/login");
      } else {
        toast.error("Could not load practitioners");
      }
    } finally {
      setLoading(false);
    }
  };

  const sendInvite = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error("Name and email required"); return; }
    setInviting(true);
    try {
      const { data } = await api.post("/admin/practitioners/invite", form);
      if (data.last_invite_email_status === "sent") {
        toast.success(`Invitation sent to ${form.email}`);
      } else {
        toast.warning("Invite created, but email delivery failed. Use 'Copy Invite Link' to send it manually.", { duration: 6000 });
      }
      setForm({ name: "", email: "", clinic_name: "", role: "practitioner_lite", phone: "", notes: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Could not send invitation"));
    } finally {
      setInviting(false);
    }
  };

  const resend = async (id) => {
    setBusyId(id);
    try {
      const { data } = await api.post(`/admin/practitioners/${id}/resend-invite`);
      if (data.status === "sent") {
        toast.success("Invitation resent");
      } else {
        toast.warning(`Email ${data.status}. Use 'Copy Invite Link' to send manually.`, { duration: 6000 });
      }
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Could not resend invitation"));
    } finally {
      setBusyId(null);
    }
  };

  const copyInviteLink = async (id) => {
    setBusyId(id);
    try {
      const { data } = await api.get(`/admin/practitioners/${id}/invite-link`);
      try {
        await navigator.clipboard.writeText(data.invite_url);
        toast.success("Invite link copied to clipboard", { duration: 5000 });
      } catch {
        // Clipboard API can fail on insecure contexts — prompt fallback
        window.prompt("Copy the invite link below and send it to the practitioner manually:", data.invite_url);
      }
    } catch (err) {
      toast.error(formatApiError(err, "Could not generate invite link"));
    } finally {
      setBusyId(null);
    }
  };

  const toggle = async (row) => {
    setBusyId(row.id);
    try {
      const action = row.is_active ? "deactivate" : "reactivate";
      await api.post(`/admin/practitioners/${row.id}/${action}`);
      toast.success(row.is_active ? "Practitioner deactivated" : "Practitioner reactivated");
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Could not update practitioner"));
    } finally {
      setBusyId(null);
    }
  };

  // ── Admin-granted trial state ─────────────────────────────────────
  const [trialCfg, setTrialCfg] = useState(null);
  const [trialForm, setTrialForm] = useState({
    email: "", name: "", clinic_name: "",
    plan: "pro", duration_days: 14, reason: "product_testing",
    reason_other: "", admin_note: "", send_invite: true,
  });
  const [trialConfirming, setTrialConfirming] = useState(false);
  const [trialBusy, setTrialBusy] = useState(false);
  const [trialLastResult, setTrialLastResult] = useState(null);
  const [trials, setTrials] = useState([]);

  useEffect(() => {
    if (!adminAuth.getToken()) return;
    api.get("/admin/practitioners/trial/config")
      .then(({ data }) => setTrialCfg(data)).catch(() => {});
    loadTrials();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTrials = () => api.get("/admin/practitioners/trials?status=all")
    .then(({ data }) => setTrials(data.items || []))
    .catch(() => setTrials([]));

  const grantTrial = async () => {
    if (!trialForm.email.trim()) { toast.error("Email required"); return; }
    setTrialBusy(true);
    try {
      const { data } = await api.post(
        "/admin/practitioners/trial/grant", trialForm);
      setTrialLastResult(data);
      toast.success(
        data.mode === "invited"
          ? `Invitation sent to ${data.email}. Trial starts on activation.`
          : `${data.plan.toUpperCase()} trial attached to ${data.email}. Access active now.`);
      setTrialConfirming(false);
      setTrialForm({ ...trialForm, email: "", name: "", clinic_name: "",
                       admin_note: "" });
      loadTrials();
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.code === "existing_paid_subscription") {
        toast.error(`Blocked: ${detail.message}`, { duration: 8000 });
      } else {
        toast.error(formatApiError(err, "Could not grant trial"));
      }
    } finally { setTrialBusy(false); }
  };

  const extendTrial = async (adminId, days = 7) => {
    setBusyId(adminId);
    try {
      const { data } = await api.post(
        `/admin/practitioners/${adminId}/trial/extend`,
        { additional_days: days });
      toast.success(`Extended by ${days} days — ${data.days_remaining} left`);
      loadTrials(); load();
    } catch (err) { toast.error(formatApiError(err, "Extend failed")); }
    finally { setBusyId(null); }
  };

  const endTrial = async (adminId) => {
    if (!window.confirm("End this trial immediately? The practitioner account and all data are preserved.")) return;
    setBusyId(adminId);
    try {
      await api.post(`/admin/practitioners/${adminId}/trial/end`, {});
      toast.success("Trial ended. Practitioner data preserved.");
      loadTrials(); load();
    } catch (err) { toast.error(formatApiError(err, "End failed")); }
    finally { setBusyId(null); }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <TopNav
        right={
          <Link to="/admin">
            <Button variant="ghost" className="rounded-full text-slate-700">
              <ArrowLeft className="h-4 w-4 mr-2" /> Back to dashboard
            </Button>
          </Link>
        }
      />
      <main className="flex-1 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 w-full" data-testid="admin-practitioners-page">
        <div className="kcis-fade-up">
          <div className="kcis-overline text-purple-900">Super admin</div>
          <h1 className="font-display text-3xl tracking-tight text-slate-900 mt-2">Practitioner management</h1>
          <p className="text-sm text-slate-600 mt-2">
            Invite practitioners, set their tier, and control account access.
          </p>
        </div>

        {/* Sandbox warning banner */}
        {senderStatus?.resend_sandbox_active && (
          <div
            className="kcis-card mt-6 p-4 flex items-start gap-3 border border-amber-200 bg-amber-50"
            data-testid="resend-sandbox-warning"
          >
            <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-900">
              <div className="font-semibold">Resend is in sandbox mode</div>
              <p className="mt-1 text-amber-800 leading-relaxed">
                {senderStatus.warning}
              </p>
            </div>
          </div>
        )}

        {/* Invite form */}
        <section className="kcis-card p-6 sm:p-8 mt-8" data-testid="practitioner-invite-form">
          <div className="kcis-overline text-purple-900">Invite a practitioner</div>
          <h2 className="font-display text-xl tracking-tight text-slate-900 mt-1">New invitation</h2>
          <form onSubmit={sendInvite} className="grid sm:grid-cols-2 gap-4 mt-5">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Full name</Label>
              <Input data-testid="invite-name"
                className="mt-1.5 rounded-xl bg-slate-50/60 border-slate-200 h-11"
                value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Dr. Jane Practitioner" required />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Email</Label>
              <Input data-testid="invite-email" type="email"
                className="mt-1.5 rounded-xl bg-slate-50/60 border-slate-200 h-11"
                value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="jane@clinic.com" required />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Clinic / business name</Label>
              <Input data-testid="invite-clinic"
                className="mt-1.5 rounded-xl bg-slate-50/60 border-slate-200 h-11"
                value={form.clinic_name} onChange={(e) => setForm({ ...form, clinic_name: e.target.value })}
                placeholder="Practice name (optional)" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Tier</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="mt-1.5 rounded-xl bg-slate-50/60 border-slate-200 h-11" data-testid="invite-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="practitioner_lite">Practitioner — Lite</SelectItem>
                  <SelectItem value="practitioner_pro">Practitioner — Pro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Phone (optional)</Label>
              <Input data-testid="invite-phone"
                className="mt-1.5 rounded-xl bg-slate-50/60 border-slate-200 h-11"
                value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-slate-500">Notes (optional)</Label>
              <Textarea data-testid="invite-notes"
                className="mt-1.5 rounded-xl bg-slate-50/60 border-slate-200 min-h-[80px]"
                value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Internal note about this practitioner" />
            </div>
            <div className="sm:col-span-2 flex justify-end">
              <Button type="submit" disabled={inviting}
                className="rounded-full bg-purple-900 hover:bg-purple-800 text-white h-11 px-6"
                data-testid="invite-submit">
                {inviting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UserPlus className="h-4 w-4 mr-2" />}
                Send invitation
              </Button>
            </div>
          </form>
        </section>

        {/* Grant Practitioner Trial (admin-granted temporary access) */}
        <section className="kcis-card p-6 sm:p-8 mt-8"
                 data-testid="grant-trial-panel">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="kcis-overline text-purple-900">Admin-granted trial</div>
              <h2 className="font-display text-xl tracking-tight text-slate-900 mt-1">
                <Sparkles className="inline h-5 w-5 mr-1 -mt-1 text-purple-900" />
                Grant Practitioner Trial
              </h2>
              <p className="text-sm text-slate-600 mt-2">
                Give a practitioner temporary access — no credit card,
                no Square subscription, no checkout. Existing paid
                subscriptions are protected: the grant is blocked, not
                silently downgraded.
              </p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 mt-5">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">
                Practitioner email
              </Label>
              <Input data-testid="trial-email" type="email"
                className="mt-1.5 rounded-xl bg-slate-50/60 border-slate-200 h-11"
                value={trialForm.email}
                onChange={(e) => setTrialForm({ ...trialForm, email: e.target.value })}
                placeholder="student@school.edu or existing@practitioner.com" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">
                Full name (required for new practitioners)
              </Label>
              <Input data-testid="trial-name"
                className="mt-1.5 rounded-xl bg-slate-50/60 border-slate-200 h-11"
                value={trialForm.name}
                onChange={(e) => setTrialForm({ ...trialForm, name: e.target.value })}
                placeholder="e.g., Jane Practitioner" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Plan</Label>
              <Select value={trialForm.plan}
                       onValueChange={(v) => setTrialForm({ ...trialForm, plan: v })}>
                <SelectTrigger data-testid="trial-plan"
                                 className="mt-1.5 rounded-xl bg-slate-50/60 border-slate-200 h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="lite">KCIS Lite</SelectItem>
                  <SelectItem value="pro">KCIS Pro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">
                Duration
              </Label>
              <Select value={String(trialForm.duration_days)}
                       onValueChange={(v) => setTrialForm({ ...trialForm, duration_days: Number(v) })}>
                <SelectTrigger data-testid="trial-duration"
                                 className="mt-1.5 rounded-xl bg-slate-50/60 border-slate-200 h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">7 days</SelectItem>
                  <SelectItem value="14">14 days (default)</SelectItem>
                  <SelectItem value="30">30 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">
                Reason
              </Label>
              <Select value={trialForm.reason}
                       onValueChange={(v) => setTrialForm({ ...trialForm, reason: v })}>
                <SelectTrigger data-testid="trial-reason"
                                 className="mt-1.5 rounded-xl bg-slate-50/60 border-slate-200 h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="product_testing">Product Testing</SelectItem>
                  <SelectItem value="prospective_practitioner">Prospective Practitioner</SelectItem>
                  <SelectItem value="complimentary_evaluation">Complimentary Evaluation</SelectItem>
                  <SelectItem value="other">Other (specify)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {trialForm.reason === "other" && (
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">
                  Reason (free text)
                </Label>
                <Input data-testid="trial-reason-other"
                  className="mt-1.5 rounded-xl bg-slate-50/60 border-slate-200 h-11"
                  value={trialForm.reason_other}
                  onChange={(e) => setTrialForm({ ...trialForm, reason_other: e.target.value })}
                  placeholder="Brief description" />
              </div>
            )}
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-slate-500">
                Internal admin note (optional)
              </Label>
              <Textarea data-testid="trial-note"
                className="mt-1.5 rounded-xl bg-slate-50/60 border-slate-200 min-h-[64px]"
                value={trialForm.admin_note}
                onChange={(e) => setTrialForm({ ...trialForm, admin_note: e.target.value })}
                placeholder="Only visible to admins" />
            </div>
            <div className="sm:col-span-2 flex items-center gap-2">
              <input type="checkbox" id="trial-send-invite"
                     checked={trialForm.send_invite}
                     onChange={(e) => setTrialForm({ ...trialForm, send_invite: e.target.checked })}
                     data-testid="trial-send-invite" />
              <label htmlFor="trial-send-invite" className="text-sm text-slate-700">
                Send invitation email (new practitioners only)
              </label>
            </div>
          </div>

          <div className="flex justify-end mt-5">
            {!trialConfirming ? (
              <Button
                onClick={() => setTrialConfirming(true)}
                disabled={!trialForm.email.trim()}
                data-testid="trial-grant-btn"
                className="rounded-full bg-purple-900 hover:bg-purple-800 text-white h-11 px-6">
                <Sparkles className="h-4 w-4 mr-2" /> Grant Trial
              </Button>
            ) : (
              <div className="w-full sm:w-auto border rounded-xl border-purple-200 bg-purple-50 p-4"
                   data-testid="trial-confirm-dialog">
                <div className="text-sm text-purple-900 font-semibold">Confirm trial grant</div>
                <ul className="text-xs text-purple-900 mt-2 space-y-0.5">
                  <li><b>Practitioner:</b> {trialForm.email}</li>
                  <li><b>Plan:</b> KCIS {trialForm.plan.toUpperCase()}</li>
                  <li><b>Duration:</b> {trialForm.duration_days} days from activation</li>
                  <li><b>Reason:</b> {trialForm.reason === "other" ? trialForm.reason_other : trialForm.reason.replace(/_/g, " ")}</li>
                  <li className="text-emerald-800"><b>No payment method required.</b></li>
                </ul>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" onClick={grantTrial} disabled={trialBusy}
                          data-testid="trial-grant-confirm-btn"
                          className="rounded-full bg-purple-900 hover:bg-purple-800 text-white">
                    {trialBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
                    Confirm & grant
                  </Button>
                  <Button size="sm" variant="ghost"
                          onClick={() => setTrialConfirming(false)}
                          data-testid="trial-grant-cancel-btn">
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>

          {trialLastResult && (
            <div className="mt-4 text-xs bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-emerald-900"
                 data-testid="trial-last-result">
              <b>Trial granted.</b> {trialLastResult.message}
              {trialLastResult.accept_url && (
                <>
                  {" "}
                  <button
                    className="underline"
                    onClick={() => {
                      try {
                        navigator.clipboard.writeText(trialLastResult.accept_url);
                        toast.success("Invite link copied");
                      } catch {
                        window.prompt("Copy the invite link:", trialLastResult.accept_url);
                      }
                    }}
                    data-testid="trial-copy-invite-btn"
                  >
                    Copy invite link
                  </button>
                </>
              )}
            </div>
          )}
        </section>

        {/* Trials management */}
        {trials.length > 0 && (
          <section className="kcis-card mt-8" data-testid="trials-table">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <div className="kcis-overline">Practitioner Trials</div>
              <span className="text-xs text-slate-500">{trials.length} total</span>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Practitioner</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Trial status</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead>Granted by</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {trials.map((t) => (
                  <TableRow key={t.admin_id} data-testid={`trial-row-${t.admin_id}`}>
                    <TableCell>
                      <div className="font-medium text-slate-900">{t.name || t.email}</div>
                      <div className="text-xs text-slate-500">{t.email}</div>
                    </TableCell>
                    <TableCell>
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${t.plan === "pro" ? "bg-purple-900 text-white border-purple-900" : "bg-purple-50 text-purple-900 border-purple-100"}`}>
                        {t.plan}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs text-slate-600">
                      {(t.reason || "").replace(/_/g, " ")}
                      {t.note ? <div className="text-slate-400">&ldquo;{t.note}&rdquo;</div> : null}
                    </TableCell>
                    <TableCell>
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${t.status === "active" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : t.status === "expired" ? "bg-amber-50 text-amber-800 border-amber-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}>
                        {t.status || "—"}
                      </span>
                      {t.status === "active" && t.days_remaining !== null && (
                        <div className="text-[10px] text-slate-500 mt-0.5 flex items-center gap-1">
                          <Clock className="h-3 w-3" /> {t.days_remaining} days remaining
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">
                      {t.expires_at ? new Date(t.expires_at).toLocaleDateString() : "—"}
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">
                      {t.granted_by || "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1.5">
                        {t.status === "active" && (
                          <>
                            <Button size="sm" variant="ghost"
                                    onClick={() => extendTrial(t.admin_id, 7)}
                                    disabled={busyId === t.admin_id}
                                    data-testid={`trial-extend-${t.admin_id}`}>
                              {busyId === t.admin_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "+ 7d"}
                            </Button>
                            <Button size="sm" variant="ghost"
                                    onClick={() => endTrial(t.admin_id)}
                                    disabled={busyId === t.admin_id}
                                    className="text-red-700"
                                    data-testid={`trial-end-${t.admin_id}`}>
                              End
                            </Button>
                          </>
                        )}
                        {t.status === "expired" && (
                          <Button size="sm" variant="ghost"
                                  onClick={() => setTrialForm({ ...trialForm, email: t.email, plan: t.plan || "pro" })}
                                  data-testid={`trial-regrant-${t.admin_id}`}>
                            Grant new
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </section>
        )}

        {/* Table */}
        <section className="kcis-card mt-8 p-2" data-testid="practitioners-table-card">
          <div className="px-4 py-4 border-b border-slate-100">
            <div className="kcis-overline">All practitioners</div>
          </div>
          {loading ? (
            <div className="p-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-purple-900" /></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Practitioner</TableHead>
                  <TableHead>Tier</TableHead>
                  <TableHead>Academic role</TableHead>
                  <TableHead>Invite</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Email delivery</TableHead>
                  <TableHead>Last login</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-12" data-testid="practitioners-empty">
                    No practitioners yet. Send your first invitation above.
                  </TableCell></TableRow>
                )}
                {rows.map((r) => (
                  <TableRow key={r.id} data-testid={`practitioner-row-${r.id}`}>
                    <TableCell>
                      <div className="font-medium text-slate-900">{r.name || r.email}</div>
                      <div className="text-xs text-slate-500">{r.email}{r.clinic_name ? ` · ${r.clinic_name}` : ""}</div>
                    </TableCell>
                    <TableCell><Badge tone={r.role}>{r.role.replace("practitioner_", "")}</Badge></TableCell>
                    <TableCell>
                      <select
                        data-testid={`academic-role-${r.id}`}
                        value={r.academic_role || ""}
                        onChange={async (e) => {
                          const v = e.target.value;
                          try {
                            await api.patch(
                              `/admin/practitioners/${r.id}/academic-role`,
                              { academic_role: v }
                            );
                            toast.success(v ? `Academic role: ${v}` : "Academic role cleared");
                            load();
                          } catch (err) {
                            toast.error(err?.response?.data?.detail || "Failed");
                          }
                        }}
                        className="rounded-md border border-slate-200 bg-white text-xs py-1 px-1.5"
                      >
                        <option value="">— none —</option>
                        <option value="academic_admin">academic_admin</option>
                        <option value="kape_reviewer">kape_reviewer</option>
                        <option value="instructor">instructor</option>
                        <option value="student">student</option>
                      </select>
                    </TableCell>
                    <TableCell><Badge tone={r.invite_status}>{r.invite_status}</Badge></TableCell>
                    <TableCell>
                      <Badge tone={r.is_active ? "active" : "inactive"}>
                        {r.is_active ? "Active" : "Inactive"}
                      </Badge>
                      {r.subscription_status && r.subscription_status !== "active" && (
                        <Badge tone={r.subscription_status}>{r.subscription_status}</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {r.invite_status === "accepted" ? (
                        <span className="text-xs text-slate-400">—</span>
                      ) : r.last_invite_email_status === "sent" ? (
                        <Badge tone="active">Sent</Badge>
                      ) : r.last_invite_email_status === "failed" ? (
                        <span
                          className="text-xs text-red-700 underline decoration-dotted cursor-help"
                          title={r.last_invite_email_error || "Unknown error"}
                          data-testid={`email-status-failed-${r.id}`}
                        >
                          Failed
                        </span>
                      ) : r.last_invite_email_status === "skipped" ? (
                        <span className="text-xs text-slate-500">Skipped</span>
                      ) : (
                        <span className="text-xs text-slate-400">Pending</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">
                      {r.last_login_at ? new Date(r.last_login_at).toLocaleString() : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1.5">
                        {r.invite_status === "pending" && r.is_active && (
                          <>
                            <Button size="sm" variant="ghost" onClick={() => copyInviteLink(r.id)} disabled={busyId === r.id}
                              data-testid={`copy-invite-link-${r.id}`}
                              className="text-purple-900 hover:bg-purple-50">
                              {busyId === r.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Copy className="h-4 w-4" />}
                              <span className="ml-1 text-xs">Copy invite link</span>
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => resend(r.id)} disabled={busyId === r.id}
                              data-testid={`resend-invite-${r.id}`}>
                              {busyId === r.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                              <span className="ml-1 text-xs">Resend</span>
                            </Button>
                          </>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => toggle(r)} disabled={busyId === r.id}
                          data-testid={`toggle-active-${r.id}`}>
                          {busyId === r.id ? <Loader2 className="h-4 w-4 animate-spin" /> :
                            r.is_active ? <PowerOff className="h-4 w-4 text-red-600" /> : <Power className="h-4 w-4 text-emerald-600" />}
                          <span className="ml-1 text-xs">{r.is_active ? "Deactivate" : "Reactivate"}</span>
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}
